/**
 * Statement import — the rows an AI reader returns from a client's documents, normalised into the
 * profile's own asset fields (owner, 15 Oct 2026).
 *
 * The reader (server/src/shared/statementExtraction.ts) is asked for a LIST per upload: a portfolio
 * report with fourteen holdings is fourteen rows; a bank statement with three accounts is three. The
 * rules it is given and that this module enforces again:
 *   - a PRODUCT (a super fund, an account-based pension, an annuity, an investment bond, a wrap or
 *     platform account) is ONE row at its account total with the product's own fields — the
 *     investment options inside it are never rows;
 *   - directly held investments (a CHESS/registry statement, a spreadsheet of shares in the client's
 *     own name) are one row per holding, with cost base and date acquired;
 *   - subtotal and total lines are never rows;
 *   - a field the document does not show is left blank, never guessed.
 *
 * Nothing here touches the profile: the adviser ticks what to add, and `appendImportedAssetDrafts`
 * (frontend) builds the asset cards from the drafts. Pure functions, so every rule is testable
 * without a document or a provider.
 */
import type { AssetType, OwnershipRef, FactFindAsset, FactFindLiability, StructureType } from './factFindTypes.ts';

/** The types a statement can land as. 'other' is deliberately absent: an unclear line is left for the adviser. */
export const IMPORTABLE_ASSET_TYPES: readonly AssetType[] = [
    'bankAccounts', 'termDeposits', 'shares', 'investmentProperty', 'investmentBonds',
    'superannuation', 'accountBasedPensions', 'annuities', 'funeralBonds', 'personalAssets', 'loansToOthers',
];

/** The fields the reader may fill for each type — the profile's own field names, nothing invented. */
export const IMPORT_FIELDS_BY_TYPE: Record<AssetType, readonly string[]> = {
    bankAccounts: ['incomeRate'],
    termDeposits: ['incomeRate', 'maturityDate'],
    shares: ['costBase', 'acquiredOn', 'incomeRate', 'frankingCredit', 'reinvest'],
    investmentProperty: ['rent', 'expenses', 'costBase', 'acquiredOn'],
    investmentBonds: ['purchasePrice', 'commencementDate'],
    superannuation: [],
    accountBasedPensions: ['income', 'commencementDate', 'purchasePrice', 'taxFree'],
    annuities: ['annuityType', 'purchasePrice', 'commencementDate', 'term', 'income', 'rcv', 'indexed', 'guaranteePeriodYears', 'reversionPct', 'fundingSource'],
    funeralBonds: ['purchasePrice', 'commencementDate'],
    prepaidFuneral: [],
    loansToOthers: ['purchasePrice', 'commencementDate', 'incomeRate'],
    personalAssets: [],
    other: [],
};

export type ImportKind = 'product' | 'direct_holding' | 'unclear' | 'home';
export type ImportConfidence = 'high' | 'medium' | 'low';

/** A trust, company or partnership the document says holds the row (15 Oct 2026). */
export interface ImportedEntity {
    name: string;
    type: StructureType;
}

const ENTITY_TYPES: readonly StructureType[] = ['family_trust', 'unit_trust', 'private_company', 'partnership', 'other'];

/** The reader's entity, or null where the row is the person's own. */
export function normaliseEntity(raw: unknown): ImportedEntity | null {
    const e = (raw || null) as { name?: unknown; type?: unknown } | null;
    const name = e ? str(e.name) : null;
    if (!name) return null;
    const t = String(e!.type || '').toLowerCase();
    const type: StructureType = ENTITY_TYPES.includes(t as StructureType) ? (t as StructureType)
        : /unit/.test(t) ? 'unit_trust' : /trust/.test(t) ? 'family_trust' : /compan|pty/.test(t) ? 'private_company' : /partner/.test(t) ? 'partnership' : 'other';
    return { name, type };
}

/** The liability types a statement can land as, in the profile's own ids. */
export const IMPORTABLE_LIABILITY_TYPES = ['mortgages', 'personalLoans', 'creditCards', 'reverseMortgages'] as const;
export type ImportableLiabilityType = (typeof IMPORTABLE_LIABILITY_TYPES)[number];

/** One liability as the reader returns it. */
export interface ExtractedLiability {
    sourceFile?: string | null;
    liabilityType?: string | null;
    lender?: string | null;
    description?: string | null;
    ownerName?: string | null;
    ownership?: string | null;
    entity?: unknown;
    balance?: number | string | null;
    interestRate?: number | string | null;
    repaymentAmount?: number | string | null;
    repaymentFrequency?: string | null;
    loanType?: string | null;
    remainingTermYears?: number | string | null;
    securedAgainst?: string | null;
    confidence?: string | null;
    notes?: string | null;
}

/** A liability ready for the review table and, on confirm, for the liabilities step. */
export interface ImportedLiabilityDraft {
    id: string;
    sourceFile: string;
    liabilityTypeId: ImportableLiabilityType | null;
    description: string;
    balance: string;
    ownership: OwnershipRef;
    entity: ImportedEntity | null;
    confidence: ImportConfidence;
    selected: boolean;
    fields: Partial<FactFindLiability>;
    flags: string[];
    notes: string | null;
}

/** One row as the reader returns it — loosely typed on purpose: it is model output. */
export interface ExtractedRow {
    sourceFile?: string | null;
    kind?: string | null;
    assetType?: string | null;
    description?: string | null;
    provider?: string | null;
    productName?: string | null;
    ownerName?: string | null;
    ownership?: string | null;
    value?: number | string | null;
    confidence?: string | null;
    fields?: Record<string, unknown> | null;
    fieldsOnStatement?: string[] | null;
    notes?: string | null;
    /** The trust, company or partnership that holds it, where the document says so. */
    entity?: unknown;
}

/** A row ready for the review table and, on confirm, for the asset list. */
export interface StatementImportDraft {
    id: string;
    sourceFile: string;
    kind: ImportKind;
    assetTypeId: AssetType | null;
    description: string;
    value: string;
    ownership: OwnershipRef;
    /** Set where an entity holds it; on confirm the ownership becomes that structure's `entity:<id>`. */
    entity: ImportedEntity | null;
    confidence: ImportConfidence;
    selected: boolean;
    /** The profile fields the document gave, in the profile's own names and units. */
    fields: Partial<FactFindAsset>;
    /** How many of the type's fields were found, for "7 of 9 fields found". */
    fieldsFound: number;
    fieldsAsked: number;
    /** Why a row is unticked or needs a look: 'already on the profile', 'owner not shown', 'type unclear'. */
    flags: string[];
    notes: string | null;
}

export interface ImportHousehold {
    isCouple: boolean;
    clientName?: string | null;
    partnerName?: string | null;
}

const num = (v: unknown): number | null => {
    if (v == null || v === '') return null;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[$,\s]/g, ''));
    return Number.isFinite(n) ? n : null;
};
const str = (v: unknown): string | null => {
    if (typeof v !== 'string') return null;
    const t = v.trim();
    // "N/A", a dash, "none": the document's way of saying nothing.
    return t && !/^(n\/?a|none|nil|-|—|–)$/i.test(t) ? t : null;
};

/** A document's title is not an asset's name (owner, 15 Oct 2026: "Annual Member Statement" landed as the description). */
const DOCUMENT_TITLE = /\b(statement|summary|schedule|report|letter|notice|confirmation|certificate)\b/i;

/** "AustralianSuper — Accumulation": the provider and the product, never the paper they came on. */
export function assetNameOf(row: ExtractedRow): string {
    const provider = str(row.provider);
    const product = str(row.productName);
    const described = str(row.description);
    if (provider && product) return product.toLowerCase().startsWith(provider.toLowerCase()) ? product : `${provider} — ${product}`;
    if (provider) return described && !DOCUMENT_TITLE.test(described) && described.toLowerCase() !== provider.toLowerCase() ? `${provider} — ${described}` : provider;
    if (described && !DOCUMENT_TITLE.test(described)) return described;
    // Only a document title to go on: no name, and the normaliser falls back to the type and file.
    return product || '';
}
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : v === 'true' ? true : v === 'false' ? false : null);

/** Whole-dollar payments per year for the frequencies a statement states. */
const PER_YEAR: Record<string, number> = { weekly: 52, fortnightly: 26, monthly: 12, quarterly: 4, 'half-yearly': 2, halfyearly: 2, annually: 1, yearly: 1, annual: 1 };

const isoDate = (v: unknown): string | null => {
    const s = str(v);
    if (!s) return null;
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return s;
    const dmy = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
    if (dmy) return `${dmy[3]}-${dmy[2]!.padStart(2, '0')}-${dmy[1]!.padStart(2, '0')}`;
    return null;
};
const isoMonth = (v: unknown): string | null => {
    const s = str(v);
    if (!s) return null;
    if (/^\d{4}-\d{2}$/.test(s)) return s;
    const d = isoDate(s);
    if (d) return d.slice(0, 7);
    const my = s.match(/^(\d{1,2})[/-](\d{4})$/);
    return my ? `${my[2]}-${my[1]!.padStart(2, '0')}` : null;
};

/** Who a row belongs to, from the reader's answer and the household; 'client' with a flag where it did not say. */
function ownershipOf(row: ExtractedRow, household: ImportHousehold, flags: string[]): OwnershipRef {
    const o = (row.ownership || '').toLowerCase();
    if (o === 'joint') return household.isCouple ? 'joint' : 'client';
    if (o === 'partner') return household.isCouple ? 'partner' : 'client';
    if (o === 'client') return 'client';
    const name = (row.ownerName || '').toLowerCase();
    const first = (s: string | null | undefined) => (s || '').toLowerCase().split(/\s+/)[0] || '';
    if (name && household.isCouple && first(household.partnerName) && name.includes(first(household.partnerName))) return 'partner';
    if (name && first(household.clientName) && name.includes(first(household.clientName))) return 'client';
    flags.push('owner not shown — set to the client');
    return 'client';
}

/** The type's fields the reader filled, in the profile's names and units. */
function fieldsOf(type: AssetType, row: ExtractedRow): Partial<FactFindAsset> {
    const f = (row.fields || {}) as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    const wanted = new Set(IMPORT_FIELDS_BY_TYPE[type]);
    const put = (key: string, v: unknown) => { if (wanted.has(key) && v != null) out[key] = v; };

    put('purchasePrice', num(f.purchasePrice));
    put('costBase', num(f.costBase));
    put('acquiredOn', isoMonth(f.acquiredOn));
    put('rent', num(f.rent));
    put('expenses', num(f.expenses));
    put('incomeRate', num(f.incomeRate));
    put('frankingCredit', num(f.frankingCredit));
    put('reinvest', bool(f.reinvest));
    put('maturityDate', isoDate(f.maturityDate));
    put('taxFree', num(f.taxFreePart ?? f.taxFree));
    put('rcv', num(f.rcv ?? f.residualCapitalValue));
    put('indexed', bool(f.indexed));
    put('guaranteePeriodYears', num(f.guaranteePeriodYears));
    put('reversionPct', num(f.reversionPct));
    put('term', num(f.term));
    const commencement = isoDate(f.commencementDate);
    if (commencement) {
        put('commencementDate', commencement);
        // The profile's own switch for the 1 January 2015 income-stream rule.
        if (type === 'accountBasedPensions') out.commencementDateOption = commencement >= '2015-01-01' ? 'post-2015' : 'pre-2015';
    }
    // A payment stated per period becomes the annual figure the profile holds. A schedule often prints
    // the ANNUAL income with the frequency it is paid beside it; multiplied up, $28,882 "fortnightly"
    // became $750,932 on a $412,600 pension (15 Oct 2026). A per-period amount that would exceed half
    // the balance (or purchase price) each year cannot be per period, so it is taken as annual.
    const annual = num(f.annualIncome);
    const perPayment = num(f.paymentAmount);
    const perYear = PER_YEAR[String(f.paymentFrequency || '').toLowerCase()] ?? null;
    if (annual != null) put('income', annual);
    else if (perPayment != null && perYear != null) {
        const base = Math.max(num(row.value) ?? 0, num(f.purchasePrice) ?? 0);
        const implausible = base > 0 && perPayment * perYear > base * 0.5;
        put('income', Math.round(implausible ? perPayment : perPayment * perYear));
    } else if (perPayment != null) put('income', Math.round(perPayment));
    const at = String(f.annuityType || '').toLowerCase();
    if (at === 'lifetime' || at === 'fixed_term') put('annuityType', at);
    else if (/fixed|term/.test(at)) put('annuityType', 'fixed_term');
    else if (/life/.test(at)) put('annuityType', 'lifetime');
    const fs = String(f.fundingSource || '').toLowerCase();
    if (fs === 'super' || fs === 'ordinary') put('fundingSource', fs);
    else if (/super/.test(fs)) put('fundingSource', 'super');
    else if (/ordinary|non-super|personal/.test(fs)) put('fundingSource', 'ordinary');
    return out as Partial<FactFindAsset>;
}

let seq = 0;
const nextId = () => `import-${Date.now().toString(36)}-${(seq += 1)}`;

/** The reader's rows as review drafts. Totals, empty rows and anything without a value are dropped. */
export function normaliseExtractedRows(rows: ExtractedRow[], household: ImportHousehold, fallbackFile = 'statement'): StatementImportDraft[] {
    const out: StatementImportDraft[] = [];
    for (const row of rows || []) {
        const description = assetNameOf(row);
        const value = num(row.value);
        if (!description && value == null) continue;
        if (/^(sub)?total\b|\btotal (portfolio|assets|balance|value)\b/i.test(description)) continue;
        // The family home is not an asset row: it fills the value on Living Arrangements, and only where
        // nothing is recorded there yet (owner, 16 Oct 2026 — never a duplicate, never an overwrite).
        if (String(row.assetType) === 'principalHome') {
            out.push({
                id: nextId(), sourceFile: str(row.sourceFile) || fallbackFile, kind: 'home', assetTypeId: null,
                description: `Family home${description ? ` — ${description.replace(/^family home\s*[—-]?\s*/i, '')}` : ''}`,
                value: value == null ? '' : String(Math.round(value)), ownership: 'client', entity: null,
                confidence: row.confidence === 'high' || row.confidence === 'medium' || row.confidence === 'low' ? row.confidence : 'low',
                selected: value != null, fields: {}, fieldsFound: 0, fieldsAsked: 0, flags: [], notes: str(row.notes),
            });
            continue;
        }
        const flags: string[] = [];
        const type = IMPORTABLE_ASSET_TYPES.includes(row.assetType as AssetType) ? (row.assetType as AssetType) : null;
        if (!type) flags.push('type unclear — add by hand if needed');
        const kindRaw = String(row.kind || '').toLowerCase();
        const kind: ImportKind = kindRaw === 'product' || kindRaw === 'direct_holding' ? kindRaw : 'unclear';
        const confidence: ImportConfidence = row.confidence === 'high' || row.confidence === 'medium' || row.confidence === 'low' ? row.confidence : 'low';
        // An entity's row is owned by the entity; otherwise an owner is only worth flagging on a row that can be added.
        // Money the person lent TO an entity is their own asset — the entity named on it is the borrower, not the holder.
        const entity = type === 'loansToOthers' ? null : normaliseEntity(row.entity);
        const ownerFlags: string[] = [];
        const ownership = entity ? 'client' : ownershipOf(row, household, ownerFlags);
        if (type && !entity) flags.push(...ownerFlags);
        const fields = type ? fieldsOf(type, row) : {};
        const asked = type ? IMPORT_FIELDS_BY_TYPE[type].length : 0;
        const found = Object.keys(fields).filter((k) => k !== 'commencementDateOption').length;
        out.push({
            id: nextId(),
            sourceFile: str(row.sourceFile) || fallbackFile,
            kind,
            assetTypeId: type,
            description: description || `${type ?? 'Asset'} from ${str(row.sourceFile) || fallbackFile}`,
            value: value == null ? '' : String(Math.round(value)),
            ownership,
            entity,
            confidence,
            selected: !!type && value != null,
            fields,
            fieldsFound: found,
            fieldsAsked: asked,
            flags,
            notes: str(row.notes),
        });
    }
    return out;
}

/**
 * The profile's assets as the duplicate check needs them: a name and a value per row, nothing else. In
 * particular NO ids — the server's id gate refuses any raw UUID in a request body (BAD_ID), which is
 * exactly what a second import hit once the first one's assets were on the profile (15 Oct 2026).
 */
export function assetsForDuplicateCheck(assets: Record<string, unknown> | undefined, home?: { value?: unknown } | null): Record<string, Array<{ description: string; value: number | null }>> {
    const out: Record<string, Array<{ description: string; value: number | null }>> = {};
    // The home as recorded on Living Arrangements, so an imported home value is offered only where that is blank.
    const homeValue = num(home?.value);
    if (homeValue != null && homeValue > 0) out.__home = [{ description: 'home', value: homeValue }];
    for (const [type, list] of Object.entries(assets || {})) {
        if (!Array.isArray(list)) continue;
        out[type] = list.map((a) => {
            const row = (a || {}) as { description?: unknown; value?: unknown };
            return { description: typeof row.description === 'string' ? row.description : '', value: num(row.value) };
        });
    }
    return out;
}

/** A draft that looks like an asset already on the profile is flagged and unticked, so a re-import does not double it. */
export function flagDuplicates(drafts: StatementImportDraft[], assets: Partial<Record<AssetType, Array<Partial<FactFindAsset>>>> | undefined): StatementImportDraft[] {
    const norm = (s: unknown) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return drafts.map((d) => {
        if (d.kind === 'home') {
            const recorded = num(((assets as Record<string, unknown> | undefined)?.__home as Array<{ value?: unknown }> | undefined)?.[0]?.value);
            return recorded != null && recorded > 0
                ? { ...d, selected: false, flags: [...d.flags, `already recorded on Living Arrangements (${new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(recorded)})`] }
                : { ...d, flags: [...d.flags, 'fills the home value on Living Arrangements'] };
        }
        if (!d.assetTypeId) return d;
        const existing = (assets?.[d.assetTypeId] || []) as Array<Partial<FactFindAsset>>;
        const value = num(d.value);
        const dup = existing.some((a) => {
            const sameValue = value != null && num(a.value) != null && Math.abs((num(a.value) as number) - value) < 1;
            const sameName = norm(a.description) !== '' && norm(a.description) === norm(d.description);
            return sameName || (sameValue && norm(a.description).includes(norm(d.description).slice(0, 6)));
        });
        return dup ? { ...d, selected: false, flags: [...d.flags, 'looks like it is already on the profile'] } : d;
    });
}

/**
 * Several directly held rows of one type and owner, as the one line an adviser may prefer: value and
 * cost base summed, the earliest acquisition kept, the names joined. Null where the rows do not all
 * share a type and owner, or where any is a product.
 */
export function combineHoldings(drafts: StatementImportDraft[]): StatementImportDraft | null {
    const rows = drafts.filter((d) => d.selected && d.assetTypeId);
    if (rows.length < 2) return null;
    const type = rows[0]!.assetTypeId;
    const owner = rows[0]!.ownership;
    if (rows.some((d) => d.assetTypeId !== type || d.ownership !== owner || d.kind === 'product')) return null;
    const sum = (key: 'value') => rows.reduce((t, d) => t + (num(d[key]) ?? 0), 0);
    const costBases = rows.map((d) => num(d.fields.costBase)).filter((v): v is number => v != null);
    const acquired = rows.map((d) => d.fields.acquiredOn).filter((v): v is string => typeof v === 'string' && v !== '').sort();
    const fields: Partial<FactFindAsset> = {};
    if (costBases.length === rows.length) fields.costBase = Math.round(costBases.reduce((a, b) => a + b, 0));
    if (acquired.length) fields.acquiredOn = acquired[0];
    return {
        id: nextId(),
        sourceFile: rows[0]!.sourceFile,
        kind: 'direct_holding',
        assetTypeId: type,
        description: rows.length <= 3 ? rows.map((d) => d.description).join(', ') : `${rows[0]!.description} and ${rows.length - 1} other holdings`,
        value: String(Math.round(sum('value'))),
        ownership: owner,
        confidence: rows.every((d) => d.confidence === 'high') ? 'high' : 'medium',
        selected: true,
        fields,
        fieldsFound: Object.keys(fields).length,
        fieldsAsked: IMPORT_FIELDS_BY_TYPE[type!].length,
        entity: rows[0]!.entity,
        flags: [],
        notes: `Combined from ${rows.length} holdings`,
    };
}

const LIABILITY_PER_YEAR: Record<string, 'weekly' | 'fortnightly' | 'monthly'> = { weekly: 'weekly', fortnightly: 'fortnightly', monthly: 'monthly' };

/** The reader's liabilities as review drafts — including an entity's debt to the person who lent to it, which sits beside their loansToOthers asset. */
export function normaliseExtractedLiabilities(rows: ExtractedLiability[], household: ImportHousehold, fallbackFile = 'statement'): ImportedLiabilityDraft[] {
    const out: ImportedLiabilityDraft[] = [];
    for (const row of rows || []) {
        const lender = str(row.lender);
        const described = str(row.description);
        const description = lender && described && !described.toLowerCase().startsWith(lender.toLowerCase()) ? `${lender} — ${described}` : (described || lender || '');
        const balance = num(row.balance);
        if (!description && balance == null) continue;
        if (/^(sub)?total\b/i.test(description)) continue;
        const flags: string[] = [];
        const lt = String(row.liabilityType || '').toLowerCase();
        const liabilityTypeId: ImportableLiabilityType | null = (IMPORTABLE_LIABILITY_TYPES as readonly string[]).includes(lt) ? (lt as ImportableLiabilityType)
            : /reverse/.test(lt) ? 'reverseMortgages' : /mortgage|home loan|investment loan|property/.test(lt) ? 'mortgages' : /card/.test(lt) ? 'creditCards' : /loan/.test(lt) ? 'personalLoans' : null;
        if (!liabilityTypeId) flags.push('type unclear — add by hand if needed');
        const entity = normaliseEntity(row.entity);
        const ownerFlags: string[] = [];
        const ownership = entity ? 'client' : ownershipOf({ ownerName: row.ownerName, ownership: row.ownership }, household, ownerFlags);
        if (liabilityTypeId && !entity) flags.push(...ownerFlags);
        const fields: Partial<FactFindLiability> = {};
        const rate = num(row.interestRate);
        if (rate != null) fields.interestRate = rate;
        const repayment = num(row.repaymentAmount);
        const freq = LIABILITY_PER_YEAR[String(row.repaymentFrequency || '').toLowerCase()];
        if (repayment != null) { fields.repayments = repayment; fields.repaymentCalculation = 'manual'; }
        if (freq) fields.frequency = freq;
        const loanType = String(row.loanType || '').toLowerCase();
        if (loanType === 'io' || /interest only|interest-only/.test(loanType)) fields.loanType = 'io';
        else if (loanType === 'pi' || /principal/.test(loanType)) fields.loanType = 'pi';
        const term = num(row.remainingTermYears);
        if (term != null) fields.loanTerm = term;
        const confidence: ImportConfidence = row.confidence === 'high' || row.confidence === 'medium' || row.confidence === 'low' ? row.confidence : 'low';
        out.push({
            id: nextId(),
            sourceFile: str(row.sourceFile) || fallbackFile,
            liabilityTypeId,
            description: description || `${liabilityTypeId ?? 'Liability'} from ${str(row.sourceFile) || fallbackFile}`,
            balance: balance == null ? '' : String(Math.round(balance)),
            ownership,
            entity,
            confidence,
            selected: !!liabilityTypeId && balance != null,
            fields,
            flags,
            // "Secured against: Unsecured" is not a note.
            notes: [str(row.securedAgainst) && !/^unsecured$/i.test(str(row.securedAgainst)!) ? `Secured against ${str(row.securedAgainst)}` : null, str(row.notes)].filter(Boolean).join('. ') || null,
        });
    }
    return out;
}

/**
 * One key for one entity, however it was written: "The Wright Family Trust", "Wright Family Trust" and
 * "WRIGHT FAMILY TRUST." are the same trust (16 Oct 2026: the import created a second one beside the
 * adviser's). A leading "the", case, punctuation and spacing are ignored.
 */
export function entityNameKey(name: string | null | undefined): string {
    return String(name || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^the /, '');
}

/** The entities the ticked rows name, once each (by entityNameKey). */
export function entitiesIn(assets: StatementImportDraft[], liabilities: ImportedLiabilityDraft[]): ImportedEntity[] {
    const seen = new Map<string, ImportedEntity>();
    [...assets, ...liabilities].forEach((d) => {
        if (d.entity && d.selected) {
            const key = entityNameKey(d.entity.name);
            if (!seen.has(key)) seen.set(key, d.entity);
        }
    });
    return Array.from(seen.values());
}

export function importedLiabilityTypeLabel(id: ImportableLiabilityType | null): string {
    const labels: Record<ImportableLiabilityType, string> = { mortgages: 'Mortgage / loan', personalLoans: 'Personal loan', creditCards: 'Credit card', reverseMortgages: 'Reverse mortgage' };
    return id ? labels[id] : '—';
}

export function importedTypeLabel(assetTypeId: AssetType | null, kind?: ImportKind): string {
    if (kind === 'home') return 'Family home — Living Arrangements';
    const labels: Partial<Record<AssetType, string>> = {
        bankAccounts: 'Bank account', termDeposits: 'Term deposit', shares: 'Shares / managed funds', investmentProperty: 'Investment property',
        investmentBonds: 'Investment bond', superannuation: 'Superannuation', accountBasedPensions: 'Account-based pension', annuities: 'Annuity', funeralBonds: 'Funeral bond',
        personalAssets: 'Personal asset', loansToOthers: 'Money lent to others',
    };
    return assetTypeId ? labels[assetTypeId] || assetTypeId : '—';
}
